import React from 'react';
import { XIcon } from 'lucide-react';
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'privacy' | 'terms' | null;
}
export const LegalModal: React.FC<LegalModalProps> = ({
  isOpen,
  onClose,
  type
}) => {
  if (!isOpen || !type) return null;
  const content = {
    privacy: {
      title: '🔒 PRIVACY POLICY',
      sections: [{
        title: 'Effective Date: January 1, 2025',
        content: 'Website: legendaryreads.com\nPublisher: Legendary Reads ("we," "our," "us")'
      }, {
        title: '1. Information We Collect',
        content: 'We collect basic information to process orders, including your name, email address, billing/shipping address, and payment information. We do not store or have direct access to credit card details — all transactions are securely processed through our payment partners (Shopify Payments, PayPal, etc.).'
      }, {
        title: '2. Use of Information',
        content: 'Your information is used solely for:\n\n• Processing and delivering orders\n• Sending order confirmations and updates\n• Customer support communication\n• Improving our store and product experience\n\nWe never sell, rent, or share your information with third parties for marketing purposes.'
      }, {
        title: '3. Cookies and Analytics',
        content: 'Our website may use cookies to enhance user experience and analyze site performance. By using our site, you consent to the use of cookies in accordance with this policy.'
      }, {
        title: '4. Data Security',
        content: 'We implement appropriate technical and organizational measures to protect your personal data from unauthorized access, disclosure, alteration, or destruction.'
      }, {
        title: '5. External Links',
        content: 'Our site may contain links to external websites (e.g., YouTube, Printful, or affiliate partners). We are not responsible for the content or privacy practices of those third-party sites.'
      }, {
        title: '6. Minors',
        content: 'We do not knowingly collect or solicit information from children under 13 years of age.'
      }, {
        title: '7. Disclaimer – Fan Publication Notice',
        content: 'This publication and website are independent fan creations not sponsored, endorsed, or affiliated with Universal City Studios LLC or Amblin Entertainment. All trademarks, including "Back to the Future™," are the property of their respective owners and are used under fair use for descriptive and historical reference.'
      }, {
        title: '8. Changes to This Policy',
        content: 'We may update this Privacy Policy periodically. Updates will be posted on this page with a revised effective date.'
      }, {
        title: '9. Contact',
        content: 'If you have questions or requests regarding your data, please contact us.'
      }]
    },
    terms: {
      title: '🧾 TERMS OF SERVICE',
      sections: [{
        title: 'Effective Date: January 1, 2025',
        content: 'Website: legendaryreads.com\nPublisher: Legendary Reads ("we," "our," "us")'
      }, {
        title: '1. Overview',
        content: 'By accessing or purchasing from legendaryreads.com, you agree to these Terms of Service. This website offers collectible books, digital editions, and related merchandise created as independent fan tributes.'
      }, {
        title: '2. Independent Fan Edition Disclaimer',
        content: 'This publication, including all its content, artwork, commentary, and analysis, is an unofficial fan-made edition created for educational, historical, and entertainment purposes. It is not affiliated with, endorsed by, or licensed by Universal City Studios LLC or Amblin Entertainment. "Back to the Future™" and all related names, logos, and characters are trademarks of Universal City Studios LLC.\n\nAll original written material, design, commentary, compilation, and layout appearing in this publication are protected under copyright law as original works of authorship owned by Legendary Reads.'
      }, {
        title: '3. Copyright and Intellectual Property',
        content: 'All original text, research, commentary, and images appearing on this website are © 2025 Legendary Reads. Unauthorized reproduction, redistribution, or commercial use of any material is strictly prohibited. Use of names or references to the Back to the Future films is for descriptive and comparative purposes only under fair use principles.'
      }, {
        title: '4. Purchases, Delivery & Refunds',
        content: "We want you to be completely satisfied with your purchase. Due to the nature of our products (digital books, physical books, mugs, prints, and tote bags), our policy is focused on replacements for items that arrive damaged or defective during shipping.\n\n1. 📖 Digital Products (E-books and others)\n\nDelivery: Instant Download.\n\nAll sales of digital products are final and are not eligible for a refund.\n\nDue to the instant and irreversible nature of the delivery of this type of content, we cannot process cancellations or refunds once the purchase has been completed and access has been granted.\n\n2. 🎁 Physical Products (Books, Mugs, Prints, Bags, etc)\n\nEstimated Delivery 13 - 15 business days for all physical products except for Time Traveler's Collector Bundle 8 - 10 business days.\n\nWe do not offer returns or refunds for physical products, with the sole exception of items that arrive damaged or defective.\n\nIf a physical item (book, mug, print, tote bag, etc.) arrives damaged or presents a manufacturing defect, we will gladly send you a replacement of the same product.\n\nTo request a replacement, you must contact us within 7 calendar days of your order's delivery date. Please include clear photos of the damage so we can process your request quickly.\n\nAfter 7 days, we unfortunately cannot offer a replacement."
      }, {
        title: '5. Limitation of Liability',
        content: 'We are not responsible for any indirect, incidental, or consequential damages resulting from the use or inability to use this website or products. This publication is a fan-made historical and educational tribute, and should not be interpreted as an official studio release.'
      }, {
        title: '6. Governing Law',
        content: 'These Terms are governed by and construed in accordance with the laws of the State of Florida, USA, without regard to conflict of law principles.'
      }, {
        title: '7. Contact',
        content: 'For any questions about these Terms or our products, contact us.'
      }]
    }
  };
  const currentContent = content[type];
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose} data-id="element-302">
      <div className="bg-bttf-bg border-2 border-bttf-accent/40 rounded-lg shadow-[0_0_30px_rgba(0,195,255,0.3)] max-w-3xl w-full max-h-[85vh] overflow-hidden relative flex flex-col" onClick={e => e.stopPropagation()} data-id="element-303">
        {/* Header */}
        <div className="bg-bttf-bg border-b border-bttf-accent/30 px-6 py-4 flex items-center justify-between flex-shrink-0" data-id="element-304">
          <h2 className="text-2xl font-bold font-future bg-gradient-main text-transparent bg-clip-text" data-id="element-305">
            {currentContent.title}
          </h2>
          <button onClick={onClose} className="text-bttf-subtext hover:text-bttf-text transition-colors p-2 hover:bg-bttf-accent/10 rounded-full" aria-label="Close" data-id="element-306">
            <XIcon className="h-6 w-6" data-id="element-307" />
          </button>
        </div>
        {/* Content */}
        <div className="overflow-y-auto flex-1 px-6 py-6 pb-24" data-id="element-308">
          {currentContent.sections.map((section, index) => <div key={index} className="mb-6" data-id="element-309">
              <h3 className="text-lg font-bold text-bttf-accent mb-2" data-id="element-310">
                {section.title}
              </h3>
              <p className="text-bttf-subtext whitespace-pre-line leading-relaxed" data-id="element-311">
                {section.content}
              </p>
            </div>)}
        </div>
        {/* Footer */}
        <div className="bg-bttf-bg border-t border-bttf-accent/30 px-6 py-4 flex-shrink-0" data-id="element-312">
          <button onClick={onClose} className="w-full bg-bttf-primary hover:bg-bttf-primary-hover text-bttf-text font-bold py-3 rounded-md transition-all" data-id="element-313">
            Close
          </button>
        </div>
      </div>
    </div>;
};